/* eslint-disable camelcase */
import React, { useEffect, useMemo } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup,
  Card,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '~/pages/Quality/styles';
import {
  getSieveOpenings,
  getMaterialSpecifications,
  getUpperSieveOpenings,
  getLowerSieveOpenings,
  getControlSieves,
  updateGranulometries,
  resetGranulometries,
  setGranulometryValue,
} from '~/store/quality/actions';
import { getGranulometriesV2 } from '~/store/quality/actions_v2';
import PF2MDataTable from '~/components/PF2MDataTable';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MOutlinedInput from '~/components/PF2MOutlinedInput';
import PF2MSearchButton from '~/components/PF2MSearchButton';
import PF2MClearFiltersButton from '~/components/PF2MClearFiltersButton';
import { Field, Form, Formik } from 'formik';
import { getElements, getMaterials, getSubElements } from '~/store/nref/actions';
import PF2MAddButton from '~/components/PF2MAddButton';
import PF2MDiscardChangesButton from '~/components/PF2MDiscardChangesButton';
import i18n from '~/i18n';
import PF2MDatepickerNew from '~/components/PF2MDatepickerNew';
import { newMomentDate, parseDateTime } from '~/utils/moment';

const SEARCH_LIMIT_IN_DAYS = process.env.REACT_APP_SEARCH_LIMIT_IN_DAYS || 7;

const buildSearchParams = (start, end) => ({
  start_date: start.format('DD/MM/YYYY'),
  start_time: start.format('HH:mm:ss'),
  end_date: end.format('DD/MM/YYYY'),
  end_time: end.format('HH:mm:ss'),
});

const defaultFilterStartDate = () => newMomentDate()
  .subtract(Number(SEARCH_LIMIT_IN_DAYS) - 1, 'days')
  .startOf('day');
const defaultFilterEndDate = () => newMomentDate().endOf('day');

const AnalysisTab = ({ classes }) => {
  const sieveOpenings = useSelector(state => state.quality.sieveOpenings);
  const upperSieveOpenings = useSelector(state => state.quality.upperSieveOpenings);
  const lowerSieveOpenings = useSelector(state => state.quality.lowerSieveOpenings);
  const controlSieves = useSelector(state => state.quality.controlSieves);
  const materialSpecifications = useSelector(state => state.quality.materialSpecifications);
  const materials = useSelector(state => state.nref.materials);
  const elements = useSelector(state => state.nref.elements);
  const elementPoints = useSelector(state => state.nref.subElements);
  const granulometries = useSelector(state => state.quality.granulometries);

  const tableData = useMemo(() => granulometries
    .filter(g => g.active)
    .map(g => ({
      ...g,
      start_datetime: g.start_date && g.start_time ? `${g.start_date} ${g.start_time}` : '',
      end_datetime: g.end_date && g.end_time ? `${g.end_date} ${g.end_time}` : '',
    })), [granulometries]);

  const [selectedMaterialSpecification, setSelectedMaterialSpecification] = React.useState(null);
  const [controlSievesValues, setControlSievesValues] = React.useState([]);
  const [filterStartDate, setFilterStartDate] = React.useState(defaultFilterStartDate);
  const [filterEndDate, setFilterEndDate] = React.useState(defaultFilterEndDate);
  const [searchParams, setSearchParams] = React.useState(
    () => buildSearchParams(defaultFilterStartDate(), defaultFilterEndDate()),
  );

  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const getDateRangeError = (start, end) => {
    if (!start || !start.isValid()) {
      return translate('validation:InitialDateRequired');
    }
    if (!end || !end.isValid()) {
      return translate('validation:FinalDateRequired');
    }
    if (end.isBefore(start)) {
      return translate('validation:FinalDateSmallerThanInitialDate');
    }
    const startOfDayStart = start.clone().startOf('day');
    const startOfDayEnd = end.clone().startOf('day');
    const rangeInDays = startOfDayEnd.diff(startOfDayStart, 'days') + 1;
    if (rangeInDays > SEARCH_LIMIT_IN_DAYS) {
      const searchTimeRangeMsg = translate('validation:SearchTimeRangeCantExceed');
      const daysMsg = translate('common:Days').toLowerCase();
      return `${searchTimeRangeMsg} ${SEARCH_LIMIT_IN_DAYS} ${daysMsg}.`;
    }
    return '';
  };

  const dateRangeError = getDateRangeError(filterStartDate, filterEndDate);

  const handleSearch = () => {
    if (dateRangeError) return;
    setSearchParams(buildSearchParams(filterStartDate, filterEndDate));
  };

  const handleClearFilters = () => {
    const startDate = defaultFilterStartDate();
    const endDate = defaultFilterEndDate();
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
    setSearchParams(buildSearchParams(startDate, endDate));
  };

  useEffect(() => {
    async function fetchData() {
      dispatch(getSieveOpenings());
      dispatch(getMaterials());
      dispatch(getElements());
      dispatch(getSubElements());
      dispatch(getMaterialSpecifications());
    }
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(getGranulometriesV2(searchParams));
  }, [searchParams, dispatch]);

  const addNewGranulometry = (values) => {
    const { material_id, ...rest } = values;
    const newGranulometry = {
      ...rest,
      sieves: controlSievesValues,
      start_date: values.start_datetime.format('DD/MM/YYYY'),
      end_date: values.end_datetime.format('DD/MM/YYYY'),
      start_time: values.start_datetime.format('HH:mm:ss'),
      end_time: values.end_datetime.format('HH:mm:ss'),
      material_specification_id: selectedMaterialSpecification?.id || null,
      update_status: 'I',
    };
    dispatch(updateGranulometries([newGranulometry], searchParams));
  };

  const customColumns = sieveOpenings.map(sieve => ({
    field: `sieve_${sieve.id}_grains_retained`,
    title: sieve.name,
    filtering: false,
    render: (rowData) => {
      if (!rowData.sieves) {
        return (
          <span className="d-flex justify-content-center">-</span>
        );
      }
      const sieveData = rowData.sieves.find(s => s.sieve_opening_id === sieve.id);
      return sieveData ? <span className="d-flex justify-content-center">{sieveData.grains_retained}</span> : <span className="d-flex justify-content-center">-</span>;
    },
    customOnChange: ({ row, field, value }) => {
      const sieve_id = parseInt(field.split('_')[1], 10);
      const updatedGranulometry = { ...row };
      const sieveIndex = updatedGranulometry.sieves.findIndex(s => s.sieve_opening_id === sieve_id);

      if (sieveIndex >= 0) {
        updatedGranulometry.sieves[sieveIndex].grains_retained = parseFloat(value);
        updatedGranulometry[field] = parseFloat(value);
      } else {
        updatedGranulometry.sieves.push({
          sieve_opening_id: sieve_id,
          grains_retained: parseFloat(value),
          id: null,
        });
        updatedGranulometry[field] = parseFloat(value);
      }
      return updatedGranulometry;
    },
  }));

  const columns = [
    {
      field: 'start_datetime',
      title: translate('common:StartDateTime'),
      filtering: false,
      validate: (rowData) => {
        const value = rowData?.start_datetime;
        const dt = parseDateTime(value, true);
        if (!value || !dt.isValid()) {
          return { isValid: false, helperText: translate('validation:InvalidDate') };
        }
        return { isValid: true };
      },
      customOnChange: ({ value }) => {
        const dt = parseDateTime(value, true);
        if (!dt?.isValid?.() || !dt.isValid()) return {};
        return {
          start_date: dt.format('DD/MM/YYYY'),
          start_time: dt.format('HH:mm:ss'),
        };
      },
      render: rowData => parseDateTime(`${rowData.start_date} ${rowData.start_time}`, true).format(`${i18n.t('date:DateFormat')} HH:mm:ss`),
    },
    {
      field: 'end_datetime',
      title: translate('common:EndDateTime'),
      filtering: false,
      validate: (rowData) => {
        const value = rowData?.end_datetime;
        const dt = parseDateTime(value, true);
        if (!value || !dt.isValid()) {
          return { isValid: false, helperText: translate('validation:InvalidDate') };
        }

        const startValue = rowData?.start_datetime;
        const startDt = parseDateTime(startValue, true);
        if (startValue && startDt.isValid() && dt.isValid() && dt.isBefore(startDt)) {
          return {
            isValid: false,
            helperText: translate('validation:FinalDateSmallerThanInitialDate'),
          };
        }

        return { isValid: true };
      },
      customOnChange: ({ value }) => {
        const dt = parseDateTime(value, true);
        if (!dt?.isValid?.() || !dt.isValid()) return {};
        return {
          end_date: dt.format('DD/MM/YYYY'),
          end_time: dt.format('HH:mm:ss'),
        };
      },
      render: rowData => parseDateTime(`${rowData.end_date} ${rowData.end_time}`, true).format(`${i18n.t('date:DateFormat')} HH:mm:ss`),
    },
    {
      field: 'element_id',
      title: translate('common:Element'),
      lookup: elements,
      customType: 'filterLookup',
    },
    {
      field: 'element_point_id',
      title: translate('common:SubElement'),
      lookup: elementPoints,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_element) === String(rowData.element_id),
    },
    {
      field: 'material_id',
      title: translate('common:Material'),
      lookup: materials,
      customType: 'filterLookup',
      editable: false,
    },
    {
      field: 'total_mass',
      title: translate('common:TotalMassSample'),
      filtering: false,
      render: rowData => (
        <span className="d-flex justify-content-center">
          {rowData.total_mass}
        </span>
      ),
    },
    ...customColumns,
  ];

  const INITIAL_VALUES = {
    material_id: 0,
    upper_sieve_opening_id: 0,
    lower_sieve_opening_id: 0,
    total_mass: 0,
    element_id: 0,
    element_point_id: 0,
    start_datetime: newMomentDate(),
    end_datetime: newMomentDate(),
  };

  const renderItems = data => [
    { value: 0, label: translate('common:Select') },
    ...data.map(e => ({ value: e.id, label: e.name })),
  ];

  const format = i18n.t('date:DateFormat');

  const handleChangeMaterial = (e, form) => {
    const material_id = e.target.value;
    form.setFieldValue('material_id', material_id);
    dispatch(getUpperSieveOpenings({ material_id }));
  };

  const handleChangeUpperSieve = (e, form) => {
    const upper_sieve_id = e.target.value;
    form.setFieldValue('upper_sieve_opening_id', upper_sieve_id);
    const { material_id } = form.values;
    dispatch(getLowerSieveOpenings({ material_id, upper_sieve_id }));
  };

  const handleChangeLowerSieve = (e, form) => {
    const lower_sieve_id = e.target.value;
    form.setFieldValue('lower_sieve_opening_id', lower_sieve_id);
    dispatch(getControlSieves({ material_id: form.values.material_id }));
    const materialSpec = materialSpecifications.find(ms => (
      ms.material_id === form.values.material_id
      && ms.upper_sieve_opening_id === form.values.upper_sieve_opening_id
      && ms.lower_sieve_opening_id === lower_sieve_id
    ));
    setSelectedMaterialSpecification(materialSpec || null);
  };

  const specsForm = () => (
    <Formik
      initialValues={INITIAL_VALUES}
      validate={(values) => {
        const errors = {};

        // Verifica se existe um movimento com as informações fornecidas

        if (values.upper_sieve_opening_id <= 0) {
          errors.upper_sieve_opening_id = 'Required';
        }
        if (values.lower_sieve_opening_id <= 0) {
          errors.lower_sieve_opening_id = 'Required';
        }
        if (values.material_id === 0) {
          errors.material_id = 'Required';
        }
        if (values.element_point_id === 0) {
          errors.element_point_id = 'Required';
        }
        if (values.total_mass <= 0) {
          errors.total_mass = 'Required';
        }
        if (values.start_datetime >= values.end_datetime) {
          errors.end_datetime = translate('validation:FinalDateSmallerThanInitialDate');
        }

        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        console.log('values', values);
        addNewGranulometry(values);
        resetForm();
      }}
    >
      {({
        resetForm,
        isSubmitting,
        isValid,
        errors,

      }) => (
        <Form>
          <p>{translate('common:AssociationRegistry')}</p>
          <div className={classnames(classes.materialSpecsForm, 'd-flex flex-wrap justify-content-start')}>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:StartDateTime')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name={'start_datetime'}
                  component={({ form, field }) => (
                    <PF2MDatepickerNew
                      views={['year', 'month', 'date', 'hours', 'minutes', 'seconds']}
                      format={`${format} HH:mm:ss`}
                      form={form}
                      field={field}
                      isDateTime
                    />
                  )}
                  style={{
                    width: '100%',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    padding: '4px 11px',
                  }}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:EndDateTime')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name={'end_datetime'}
                  component={({ form, field }) => (
                    <PF2MDatepickerNew
                      views={['year', 'month', 'date', 'hours', 'minutes', 'seconds']}
                      format={`${format} HH:mm:ss`}
                      form={form}
                      field={field}
                      isDateTime
                    />
                  )}
                  style={{
                    width: '100%',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    padding: '4px 11px',
                  }}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:Element')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="element_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      onChange={
                        e => form.setFieldValue('element_id', e.target.value)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(elements)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>


            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:SubElement')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="element_point_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      onChange={
                        e => form.setFieldValue('element_point_id', e.target.value)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(elementPoints.filter(
                        ep => String(ep.id_element) === String(form.values.element_id),
                      ))}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:Material')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="material_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      onChange={
                        e => handleChangeMaterial(e, form)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(materials)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:UpperSieveOpening')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="upper_sieve_opening_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      disabled={upperSieveOpenings.length === 0}
                      onChange={
                        e => handleChangeUpperSieve(e, form)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(upperSieveOpenings)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:LowerSieveOpening')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="lower_sieve_opening_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      disabled={lowerSieveOpenings.length === 0}
                      onChange={
                        e => handleChangeLowerSieve(e, form)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(lowerSieveOpenings)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:TotalMassSample')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="total_mass"
                  type="number"
                  className={classnames('form-control', classes.textField, errors.total_mass
                    ? classes.fieldError : classes.typeSelector)}
                />
              </FormGroup>
            </FormControl>
          </div>

          <hr />

          {controlSieves.length > 0 && controlSieves.map((sieve, idx) => (
            <FormControl key={sieve.id} style={{ marginRight: '20px', marginBottom: '20px' }}>
              <FormLabel className={classes.formLabel}>
                {sieve.name}
              </FormLabel>
              <FormGroup>
                <Field
                  name={`sieve_${sieve.id}_mass_in_sieve`}
                  type="number"
                  component={PF2MOutlinedInput}
                  value={controlSievesValues[idx]?.grains_retained || 0}
                  onChange={(e) => {
                    const newValues = [...controlSievesValues];
                    newValues[idx] = {
                      sieve_opening_id: sieve.id,
                      grains_retained: parseFloat(e.target.value) || 0,
                    };
                    setControlSievesValues(newValues);
                  }}
                  onBlur={(e) => {
                    const newValues = [...controlSievesValues];
                    newValues[idx] = {
                      sieve_opening_id: sieve.id,
                      grains_retained: parseFloat(e.target.value).toFixed(2) || 0,
                    };
                    setControlSievesValues(newValues);
                  }}
                  className={classnames('form-control', classes.textField, errors[`sieve_${sieve.id}_mass_in_sieve`]
                    ? classes.fieldError : classes.typeSelector)}
                />
              </FormGroup>
            </FormControl>
          ))}

          <br />

          <PF2MAddButton disabled={!isValid} />
          <PF2MDiscardChangesButton onClick={() => { resetForm(); }} />

        </Form>
      )}
    </Formik>
  );

  return (
    <div className={classes.tabContainer} style={{ overflowY: 'auto', height: 'calc(100vh - 120px)' }}>
      <p>{translate('common:AssociationRegistry')}</p>
      <Card variant={'outlined'} className={classes.card}>
        {specsForm()}

      </Card>
      <Card variant={'outlined'} className={classes.card} style={{ marginTop: '20px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 200px)',
          gridColumnGap: 10,
          gridRowGap: 10,
          margin: '10px 0 40px',
        }}
        >
          <FormControl>
            <FormLabel className={classes.formLabel}>
              {translate('common:StartDate')}
            </FormLabel>
            <FormGroup>
              <PF2MDatepickerNew
                isFormControlled={false}
                value={filterStartDate}
                onChange={setFilterStartDate}
                isDateTime
                views={['year', 'month', 'date', 'hours', 'minutes', 'seconds']}
                format={translate('date:FullDateTimeFormat')}
                InputProps={{ style: { minWidth: 150, height: 40 } }}
                style={{
                  width: '100%',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  height: 38,
                  paddingLeft: 4,
                }}
              />
            </FormGroup>
          </FormControl>
          <FormControl>
            <FormLabel className={classes.formLabel}>
              {translate('common:EndDate')}
            </FormLabel>
            <FormGroup>
              <PF2MDatepickerNew
                isFormControlled={false}
                value={filterEndDate}
                onChange={setFilterEndDate}
                isDateTime
                views={['year', 'month', 'date', 'hours', 'minutes', 'seconds']}
                format={translate('date:FullDateTimeFormat')}
                disableFuture={false}
                error={Boolean(dateRangeError)}
                helperText={dateRangeError || ''}
                InputProps={{ style: { minWidth: 150, height: 40 } }}
                style={{
                  width: '100%',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  height: 38,
                  paddingLeft: 4,
                }}
              />
            </FormGroup>
          </FormControl>
          <FormControl style={{ width: 200 }}>
            <FormLabel className={classes.formLabel} style={{ visibility: 'hidden' }}>
              {translate('common:Search')}
            </FormLabel>
            <FormGroup>
              <PF2MSearchButton
                onClick={handleSearch}
                disabled={Boolean(dateRangeError)}
                size="small"
                style={{
                  width: '100%', height: 38, minHeight: 38, margin: 0, boxShadow: 'none',
                }}
              />
            </FormGroup>
          </FormControl>
          <FormControl style={{ width: 200 }}>
            <FormLabel className={classes.formLabel} style={{ visibility: 'hidden' }}>
              {translate('common:Search')}
            </FormLabel>
            <FormGroup>
              <PF2MClearFiltersButton
                onClick={handleClearFilters}
                size="small"
                style={{
                  width: '100%', height: 38, minHeight: 38, margin: 0, boxShadow: 'none',
                }}
              />
            </FormGroup>
          </FormControl>
        </div>
        <PF2MDataTable
          onDiscard={() => dispatch(resetGranulometries())}
          onChange={(newData) => {
            newData.forEach((row) => {
              const { start_datetime, end_datetime, ...cleanRow } = row;
              dispatch(setGranulometryValue(cleanRow));
            });
          }}
          onDelete={(items) => {
            items.forEach((item) => {
              const { start_datetime, end_datetime, ...cleanItem } = item;
              dispatch(setGranulometryValue({ ...cleanItem, active: 0 }));
            });
          }}
          onApply={() => dispatch(updateGranulometries(granulometries, searchParams))}
          options={{
            selection: true,
            showTitle: false,
            filtering: true,
            hideAdd: true,
          }}
          data={tableData}
          columns={columns}
        />
      </Card>
    </div>
  );
};

AnalysisTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AnalysisTab);
